import { animate, style, transition, trigger } from '@angular/animations';
import { ConnectedPosition } from '@angular/cdk/overlay';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  TemplateRef,
  ViewChild,
  WritableSignal,
  computed,
  signal,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { CurrencyWalletService } from 'src/app/layouts/currency-wallet/currency-wallet.service';
import { SettingsService } from 'src/app/pages/settings/settings.service';
import { UserAssetsService } from 'src/app/pages/user-asset/user-assets.service';
import { EditCurrencyComponent } from 'src/app/shared/components/select-currency/edit-currency.component';
import { CurrenciesInterface } from 'src/app/shared/interfaces/deposit.interface';
import { CurrencyBalance } from 'src/app/shared/interfaces/wallet.interface';
import { CurrencyValuePipe } from 'src/app/shared/pipes/currency-value.pipe';
import { DataCollectionService } from 'src/app/shared/service/data-collection.service';
import { LayoutService } from 'src/app/shared/service/layout.service';
import { LocalStorageService } from 'src/app/shared/service/localstorage.service';
import { PopupService } from 'src/app/shared/service/popup.service';
import { LedPopupComponent } from '../led-popup/led-popup.component';

interface CurrencyDisplay {
  [uid: string]: string[];
}

@UntilDestroy()
@Component({
  selector: 'app-currency-wallet-expansion',
  templateUrl: './currency-wallet-expansion.component.html',
  styleUrls: ['./currency-wallet-expansion.component.scss'],
  animations: [
    trigger('expansion', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('0.2s ease-in-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [animate('0.2s ease-in-out', style({ opacity: 0, transform: 'translateY(20px)' }))]),
    ]),
    trigger('toggle', [
      transition('* => *', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('0.3s ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
})
export class CurrencyWalletExpansionComponent implements OnInit {
  logined!: boolean;
  currentCurrencyData!: CurrenciesInterface;
  hideSmall = false;
  allCurrencyBalance: CurrencyBalance[] = [];
  allCurrencies!: CurrenciesInterface[];

  currenciesList!: CurrenciesInterface[];
  tabToggleAnimateTick: number = 0;

  /** 更新默认货币loading状态 */
  defaultCurrencyLoading: boolean = false;

  /** userUid 默认为-1， 用于用户未登录的时候判断*/
  userUid: string = '-1';
  isH5!: boolean;

  @Input() isDiasbled!: boolean;
  @Input() size!: string;
  @Input() hideSetting!: boolean;
  @Input() hideBottom!: boolean;
  @Input() currencies!: any[];
  @Input() expanded!: boolean;
  @Input() checkedItem!: string;
  @Output() expandedChange = new EventEmitter();
  @Output() selectItem = new EventEmitter();
  @Output() selectItemHeader = new EventEmitter();

  position: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', panelClass: 'overlay-expansion' },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', panelClass: 'overlay-expansion' },
  ];
  ledData!: any;

  /** 非粘性详情 */
  nonStickyDetails: WritableSignal<CurrencyBalance | null> = signal(null);
  renderNonStickyDetails = computed(() => this.nonStickyDetails());
  @ViewChild('nonStickPopup') nonStickPopup!: TemplateRef<Element>;
  closeDialog!: MatDialogRef<Element, unknown>;

  constructor(
    private renderer: Renderer2,
    private appService: AppService,
    private userAssetsService: UserAssetsService,
    public localStorageService: LocalStorageService,

    private currencyValuePipe: CurrencyValuePipe,
    private popup: PopupService,
    private layout: LayoutService,
    private settingsService: SettingsService,
    private dataCollectionService: DataCollectionService,
    public currencyWalletService: CurrencyWalletService,
  ) {}

  ngOnInit() {
    // 初始化换算、汇率相关
    this.currencyWalletService.initConversion();

    // 订阅当前登录账号信息判断是否已登录
    this.appService.userInfo$.pipe(untilDestroyed(this)).subscribe(v => {
      this.logined = !!v;
      if (this.logined) {
        this.userUid = v?.uid as string;
      }
    });

    //订阅当前货币
    this.appService.currentCurrency$.pipe(untilDestroyed(this)).subscribe(v => {
      this.currentCurrencyData = v;
    });

    //首次准备好的时候获取所有币种数据,并初始化
    this.appService.currencies$.pipe(untilDestroyed(this)).subscribe(x => {
      if (x.length > 0) {
        this.allCurrencies = x.filter(v => v.isVisible);
        if (!this.currencies) {
          this.currencies = this.allCurrencies;
        } else {
          this.currencies = this.currencies.map(cur => {
            return { ...cur, ...this.allCurrencies.find(t => t.currency === cur.currency) };
          });
        }
        this.initData();
      }
    });
    combineLatest([this.appService.userBalance$, this.appService.mainWalletBalance$])
      .pipe(untilDestroyed(this))
      .subscribe(([userBalance, mainWalletBalance]) => {
        if (userBalance && mainWalletBalance) {
          this.allCurrencyBalance = userBalance;
          this.ledData = mainWalletBalance.currencies.find(x => x.currency == 'LED');
          this.initData();
        }
      });

    this.layout.isH5$.pipe(untilDestroyed(this)).subscribe(e => (this.isH5 = e));
  }

  //展开/收起
  handleOpen() {
    // 原创自动投注时禁止展开收起
    if (this.appService.originalAutoBet$.value || this.settingsService.setDefaultCurrencyLoading$.value) {
      this.handleClose();
      return;
    }
    this.expanded = !this.expanded;
    this.expandedChange.emit(this.expanded);
    setTimeout(() => {
      this.layout.mainContentBackdrop$.next(this.expanded);
    });
    // 定位到当前选择的tab
    if (this.expanded && this.currentCurrencyData)
      this.selectIndex = this.allCurrencies.find(cue => cue.currency === this.checkedItem)?.isDigital ? 0 : 1;
  }

  handleClose() {
    if (this.expanded) {
      this.expanded = false;
      this.expandedChange.emit(this.expanded);
      this.layout.mainContentBackdrop$.next(false);
    }
  }

  //选择
  handleSelect(item: CurrenciesInterface, isOther: boolean) {
    if (this.currentCurrencyData.currency === item.currency) return;

    // 立刻先显示变化
    this.currentCurrencyData = item;
    this.selectItemHeader.emit(this.currentCurrencyData);

    if (this.logined) {
      // 如果登录 调用设置默认货币接口, 且订阅
      this.settingsService.setUserDefaultCurrency({ defaultCurrencyType: item.currency }, item.currency);
    } else {
      // 不登录只是进行订阅
      this.settingsService.setAppServiceCurrentCurrency(item.currency);
    }

    //存入localStorageService
    this.localStorageService.currentCurrency = item.currency;
    this.handleClose();

    this.setDefaultToFait();

    // 来自“其它”，同时保存显示状态
    if (isOther) {
      item.display = true;
      this.saveCurrencyDisplay();
    }

    this.selectItem.emit(item.currency);
  }

  /**保存使用状态 */
  saveCurrencyDisplay() {
    const selectedArr: string[] = [];
    this.currencies.forEach(x => {
      if (x.display) selectedArr.push(x.currency);
    });
    const currencyDisplay: CurrencyDisplay = this.localStorageService.currencyDisplay || {};
    this.localStorageService.currencyDisplay = {
      ...currencyDisplay,
      [this.userUid]: selectedArr,
    };
  }

  selectTab(index: number) {
    // 触发动画
    this.tabToggleAnimateTick = Math.random();
    this.selectIndex = index;
  }

  /**初始化 */
  initData() {
    if (!this.currencies) return;
    const currencyDisplay = this.localStorageService.currencyDisplay;
    this.setDefaultToFait();
    this.currencies.forEach(x => {
      if (currencyDisplay) {
        const userHasLogined = Object.keys(currencyDisplay).filter(uid => this.userUid == uid).length > 0;
        if (userHasLogined) {
          //本地有数据，使用本地数据
          x.display = currencyDisplay[this.userUid].includes(x.currency);
        } else {
          x.display = true;
        }
      } else {
        //本地没数据，默认全部显示
        x.display = true;
      }
      // 恒定当前选中的为true，防止一些边界情况
      if (x.currency === this.currentCurrencyData?.currency) x.display = true;
    });

    if (this.hideSmall) {
      //隐藏没有金额的钱包
      this.currenciesList = this.currencies.filter(currencyItem => {
        const item = this.allCurrencyBalance.find(balanceItem => balanceItem.currency === currencyItem.currency);
        return item && item.balance! !== 0;
      });
    } else {
      //显示所有
      this.currenciesList = this.currencies;
    }
  }

  keyword: string = '';
  selectIndex = 0;
  showOnFaitPop!: MatDialogRef<unknown>;

  /**显示法币选择 */
  showOnFait(template: TemplateRef<any>, status: boolean) {
    if (status) {
      this.showOnFaitPop = this.popup.open(template, {
        inAnimation: 'zoomIn',
        outAnimation: 'zoomOut',
        speed: 'faster',
        autoFocus: false,
      });
      // 自动应用上次的选择
      this.currencyWalletService.updateConversion(
        true,
        this.currencyWalletService.conversionCurrency$.value.currency?.currency || '',
      );
    } else {
      this.currencyWalletService.updateConversion(false);
    }
  }

  /**选择某个用于显示的法币 */
  selectShowOnFiat(item: CurrenciesInterface) {
    this.currencyWalletService.updateConversion(true, item.currency);
  }

  /**编辑“我的币种” */
  editCurrency() {
    this.popup.open(EditCurrencyComponent, {
      inAnimation: 'zoomIn',
      outAnimation: 'zoomOut',
      speed: 'faster',
      autoFocus: false,
      data: {
        currencies: this.allCurrencies,
      },
    });
  }

  /**
   * 开启led弹窗
   */

  openLedPopup(show = false) {
    if (this.logined) {
      this.popup.open(LedPopupComponent, {
        panelClass: this.isH5 ? 'single-page-dialog-container' : '',
        speed: 'faster',
        isFull: this.isH5 ? true : false,
        data: {
          showPage2: show,
        },
      });
    } else {
      this.appService.jumpToLogin();
    }
  }

  /**
   * 展示非粘 余额详情
   *
   * @param currency
   */
  showNonStickBalanceDetail(currency: string) {
    this.nonStickyDetails.set(this.allCurrencyBalance?.find(item => item?.currency === currency) || null);
    this.closeDialog = this.popup.open(this.nonStickPopup, {
      disableClose: false,
    });
  }

  setDefaultToFait() {
    const currentCurrency =
      this.currencies?.find(cur => cur.currency === this.localStorageService.currentCurrency) || null;

    this.currencyWalletService.defaultToFiat$.next({
      currency: currentCurrency || null,
      rates: this.userAssetsService.getRatesBaseCurrency(this.localStorageService.currentCurrency).rates,
    });
  }
}
